import { filter, isEqual, merge, omit, orderBy, sumBy } from 'lodash'

import { DashboardWidgetName, DropdownListData, Sort, SortDirection } from '@types'
import { PivotTableGroup } from './enums'
import {
  PivotTableCaptions,
  PivotTableColsStructureFilter,
  PivotTableConfig,
  PivotTableData,
  PivotTableFields,
  PivotTableValueGetter,
  PivotTableValueRenderers,
} from './types'

import { GRAND_TOTAL_CAPTION } from './const'
import { MIN_HEADER_WIDTH, MIN_RESIZED_WIDTH } from './const'
import { ROUTE_NAME, UNKNOWN_VALUE } from '@/const'

import { useDashboardsStore } from '@/store/dashboards'
import { useRepositoriesStore } from '@/store/repositories'

import { generateSlug } from '@/views/Dashboards/components/utils/helpers'

export const prepareInputFields = (fields: PivotTableFields) =>
  Object.fromEntries(
    Object.entries(fields).map(([key, field], index) => [
      key,
      {
        ...field,
        index,
      },
    ]),
  )

export const getEnabledFields = (
  fields?: PivotTableFields,
  groups = [PivotTableGroup.COLS, PivotTableGroup.ROWS],
) =>
  fields &&
  orderBy(
    Object.entries(fields).filter(
      ([, field]) => field.enabled && groups.includes(field.group),
    ),
    ([, field]) => field.index,
  ).map(([key]) => key)

export const prepareCaption = (
  field: string,
  caption?: PivotTableCaptions[string],
) => {
  if (field === 'footer_grand_total') return GRAND_TOTAL_CAPTION
  // if caption is empty display the field name in uppercased
  return caption || field.at(0)?.toUpperCase() + field.slice(1)
}

export const prepareSortField =
  (captions?: PivotTableCaptions) => (value: string) => ({
    value,
    label: prepareCaption(value, captions?.[value]) as string,
  })

export const prepareValueSortField = (
  item: PivotTableColsStructureFilter,
  singleValue: boolean,
) => {
  const itemValues = Object.values(item)
  if (singleValue) {
    itemValues.pop()
  }
  return {
    value: JSON.stringify(item),
    label: itemValues.join(' '),
  }
}

export const getValuesCaption = (
  field: string,
  captions?: PivotTableCaptions,
) => {
  const caption = captions?.[field]
  return prepareCaption(field, caption)
}

const sortIterateesHandler =
  (renderers?: PivotTableValueRenderers, groupField?: string) =>
    (s: any) =>
      (item: PivotTableData) => {
        const key = s.field || s
        let value = item[key]
        if (typeof value === 'number') return value
        if (renderers?.[key]) {
          value = renderers?.[key](value, groupField, 'sort')
        }
        return value?.toLowerCase()
      }

export const handleValues = (
  values?: string[],
  captions?: PivotTableCaptions,
): Record<string, string | number>[] | undefined => {
  return values?.map(value => {
    const caption = getValuesCaption(value, captions)
    return { [value]: caption }
  })
}

const groupData = (data: PivotTableData[], group: string) => {
  const result: Record<string, PivotTableData[]> = {}
  for (let i = 0; i < data.length; i++) {
    let key = data[i][group]
    if (key === null) continue
    if (key === undefined) {
      key = UNKNOWN_VALUE
    }
    if (!result[key]) {
      result[key] = []
    }
    result[key].push(data[i])
  }
  return result
}

export const handleCols = (
  data?: PivotTableData[],
  cols?: string[],
  rows?: string[],
  anyCols?: boolean,
  sort?: Sort[],
  values?: PivotTableColsStructureFilter[],
  valueGetter?: PivotTableValueGetter,
  renderers?: PivotTableValueRenderers,
  level = 0,
) => {
  if (!data || !cols) return
  const groupField = cols[level]
  let result: PivotTableColsStructureFilter[] | undefined
  if (!groupField) {
    result =
      cols.length || !anyCols
        ? values
        : values?.map(value => ({
          grand_total: GRAND_TOTAL_CAPTION,
          ...value,
        }))
  } else {
    if (!result) result = []
    const groupedData = groupData(data, groupField)
    const keys = Object.keys(groupedData)
    for (let i = 0; i < keys.length; i++) {
      const groupValue = keys[i]
      const children = handleCols(
        groupedData[groupValue],
        cols,
        rows,
        anyCols,
        sort,
        values,
        valueGetter,
        renderers,
        level + 1,
      )
      if (children) {
        for (let i = 0; i < children.length; i++) {
          result.push({ [groupField]: groupValue, ...children[i] })
        }
      }
    }
    if (level === 0 && cols?.length && values) {
      for (let i = 0; i < values.length; i++) {
        result.push({ grand_total: GRAND_TOTAL_CAPTION, ...values[i] })
      }
    }
  }
  if (level === 0) {
    result = result?.map(item => {
      const rules = { ...item }
      const valueKey = Object.keys(rules).at(-1)
      if (!valueKey) return item
      const isGrandTotal = rules.grand_total
      delete rules[valueKey]
      delete rules.grand_total
      let value = 0
      if (valueGetter && data) {
        const nulledRules = Object.fromEntries((function* () {
          if (isGrandTotal) {
            for (const col of cols) {
              yield [col, null]
            }
          }
          if (!rows) return
          for (const row of rows) {
            yield [row, null]
          }
        })())
        value = valueGetter(data, valueKey, {
          ...nulledRules,
          ...rules
        })
      } else {
        const scope = Object.keys(rules).length > 0 ? filter(data, rules) : data
        value = sumBy(scope, valueKey)
      }
      return { footer_grand_total: value, ...item }
    })
  }
  if (!result) return
  const sortIteratees = sort?.length ? sort : cols
  const sortOrders = sort?.length
    ? sort.map(s => s.sortDirection)
    : cols.map(col => {
      return col === 'date' ? SortDirection.DESC : SortDirection.ASC
    })
  const sortedResult = orderBy(
    result,
    [
      'grand_total',
      ...sortIteratees.map(sortIterateesHandler(renderers, groupField)),
    ],
    ['desc', ...sortOrders],
  )
  return sortedResult
}

const prepareValueFilterParams = (
  rowsRules: Record<string, string>,
  rules: Record<string, string | number>,
  fields?: string[],
) => {
  const params = { ...rowsRules, ...rules }
  if (!fields) return params
  const nulledParams = Object.fromEntries(fields.map(field => [field, null]))
  return merge(nulledParams, params)
}

export const handleRows = (
  data: PivotTableData[],
  rows: string[],
  sort: Sort[],
  structure: PivotTableColsStructureFilter[] | undefined,
  renderers?: PivotTableValueRenderers,
  valueGetter?: PivotTableValueGetter,
  enabledFields?: string[],
  rules = {},
  level = 0,
) => {
  const groupField = rows[level]
  if (!groupField) return []
  const groupedData = groupData(data, groupField)
  const result: PivotTableData[] = []
  const keys = Object.keys(groupedData)
  for (let i = 0; i < keys.length; i++) {
    const groupValue = keys[i]
    const scope = groupedData[groupValue]
    const row: PivotTableData = {}
    row[groupField] = groupValue
    const rowsRules = {
      ...rules,
      [groupField]: groupValue,
    }
    if (structure) {
      for (let j = 0; j < structure.length; j++) {
        const item = structure[j]
        const valueKey = Object.keys(item).at(-1)
        if (!valueKey) return
        const rules = { ...item }
        delete rules[valueKey]
        delete rules.grand_total
        // delete rules.footer_grand_total
        let value = 0
        if (valueGetter) {
          const params = prepareValueFilterParams(
            rowsRules,
            rules,
            enabledFields,
          )
          value = valueGetter(scope, valueKey, params)
        } else {
          const colScope = filter(scope, rules)
          value = sumBy(colScope, valueKey)
        }
        const key = JSON.stringify(item)
        row[key] = value
      }
    }
    const children = handleRows(
      scope,
      rows,
      sort,
      structure,
      renderers,
      valueGetter,
      enabledFields,
      rowsRules,
      level + 1,
    )
    if (children?.length) {
      row.children = children
    }
    result.push(row)
  }
  const sortIteratees = sort.length ? sort : rows
  const sortOrders = sort.length
    ? sort.map(s => s.sortDirection)
    : rows.map(row => {
      return row === 'date' ? SortDirection.DESC : SortDirection.ASC
    })
  const sortedResult = orderBy(
    result,
    sortIteratees.map(sortIterateesHandler(renderers, groupField)),
    sortOrders,
  )
  return sortedResult
}

export const getTemplateColumn = (count: number, reset: boolean) => ({
  gridTemplateColumns: (reset
    ? ' max-content'
    : ` minmax(${MIN_RESIZED_WIDTH}px, 1fr)`
  ).repeat(count),
})

export const getColKey = (col: PivotTableColsStructureFilter) => {
  return Object.values(col).join(' ')
}

export const getTemplateColumns = (
  cols?: PivotTableColsStructureFilter[],
  sizes?: Record<string, number | string>,
) => {
  if (!cols) return {}
  return {
    gridTemplateColumns: cols
      ?.map(col => {
        const key = getColKey(col)
        let value = `minmax(${MIN_HEADER_WIDTH}px, max-content)`
        const size = sizes?.[key]
        if (typeof size === 'string') {
          value = size
        } else if (size) {
          value = `${size < MIN_RESIZED_WIDTH ? MIN_RESIZED_WIDTH : size}px`
        }
        return `${value} `
      })
      .join(' '),
  }
}

export const handleExportCols = (
  rows: PivotTableColsStructureFilter[] = [],
  structure: PivotTableColsStructureFilter[],
) => {
  const result = rows.map(row => Object.values(row)).flat(1) as string[]
  return result.concat(
    structure.map(item => Object.values(item).join(' ')),
  )
}

export const handleExportRows = (
  data: PivotTableData[],
  renderers: PivotTableValueRenderers | undefined,
) => {
  const result: string[][] = data.reduce<string[][]>((acc, row) => {
    const item = Object.entries(row).at(0)
    const field = item?.[0] || ''
    let value = item?.[1]
    if (renderers?.[field]) {
      value = renderers?.[field](value, field)
    }
    if (row.children) {
      const children = handleExportRows(row.children || [], renderers)
      children.forEach(child => {
        acc.push([value, ...child])
      })
    } else {
      const values = Object.entries(row).reduce<string[]>((acc, item) => {
        if (![field, 'children'].includes(item[0])) {
          acc.push(`${item[1]}`)
        }
        return acc
      }, [])
      acc.push([value, ...values])
    }
    return acc
  }, [])
  return result
}

export const prepareSortFields = (
  sort?: Sort[],
  columns?: DropdownListData[],
) => {
  return (
    sort?.filter(
      item =>
        item.field === '' ||
        columns?.find(column => column.value === item.field),
    ) || []
  )
}

export const compareConfigsFields = (
  storage: PivotTableConfig,
  config: PivotTableFields,
  presetConfig?: Partial<PivotTableConfig>,
) => {
  const fields = storage.fields || {}
  const storageFields = Object.keys(fields).sort()
  const configFields = Object.keys(config).sort()
  if (isEqual(storageFields, configFields)) return storage
  configFields.forEach((key, index) => {
    if (fields[key]) return
    fields[key] = { ...config[key], index }
  })
  storageFields.forEach(key => {
    if (config[key]) return
    delete fields[key]
  })
  return {
    ...presetConfig,
    ...storage,
    fields,
  }
}

export const clearColsStructure = (
  structure?: PivotTableColsStructureFilter[],
) => {
  return structure?.map(item => omit(item, 'footer_grand_total'))
}

export const pinPivotToDashboards = async (
  name: string,
  dashboardsStore: ReturnType<typeof useDashboardsStore>,
  repositoriesStore: ReturnType<typeof useRepositoriesStore>,
  widgetName: DashboardWidgetName,
  config: PivotTableConfig,
  callback: () => void,
  newIndex?: number,
) => {
  try {
    const index = dashboardsStore.getList?.filter(item =>
      new RegExp(`^${name}(\\s\\d+)?$`).test(item.name),
    )?.length
    const slugValue = name
      .toLowerCase()
      .replace(/\s/g, '-')
      .replace(/[^a-z0-9\-]/g, '')
    const duplicate = dashboardsStore.getList?.find(
      item => item.slug === slugValue,
    )
    const slug = duplicate ? generateSlug() : slugValue || generateSlug()
    await dashboardsStore.store({
      id: '',
      name: `${name}${index ? ` ${index}` : ''}`,
      slug,
      widgets: [{
        id: widgetName,
        name: widgetName,
        title: '',
        attrs: {
          configs: [config],
          index: 0,
        },
        tall: true,
        wide: true,
      }],
    })
    if (newIndex !== undefined) {
      const sortedTabs = repositoriesStore.getSortedTabs
      if (sortedTabs.length) {
        const key = `${ROUTE_NAME.DASHBOARDS_DASHBOARD}${JSON.stringify({ slug })}`
        sortedTabs.splice(newIndex, 0, key)
        await repositoriesStore.sortTabs(sortedTabs)
      }
    }
    callback()
    return slug
  } catch (error) {
    throw Error(error as string)
  }
}
